import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useParams } from "react-router-dom";
import {
  getCustomWorkspace,
  getCustomWorkspaceReports,
  saveCustomWorkspace,
} from "@/lib/serverComm";

import { useCurrentAccountId } from "@/store/authContext/authContext";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import Reports from "@/pages/Reports";
import { useNavigate } from "react-router-dom";

import { ReportForUIV2 as ReportType } from "@/types/report";
import { ReportConfigV2 } from "@/common/report-configuration";
import ReportV2 from "@/components/report/ReportV2";
import { Input } from "@/components/ui/input";
import type { CustomWorkspace } from "@/types/customWorkspace";
import EventBus from "@/lib/eventBus";

const paritaLogoCopilot = new URL(
  "../assets/peoplescape-assistant-1.png",
  import.meta.url,
).toString();

const defaultWorkspace: CustomWorkspace = {
  id: "",
  account_id: "",
  title: "",
  report_ids: [],
  created_at: "",
  updated_at: "",
};

const CustomWorkspace: React.FC = () => {
  const currentAccountId = useCurrentAccountId();
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [workspace, setWorkspace] = useState({ ...defaultWorkspace });
  const erroredReportsRef = useRef<Set<string>>(new Set());

  // retrieve the id parameter from the react router
  const { id } = useParams();
  const [persisted, setPersisted] = useState(!!id);

  useEffect(() => {
    EventBus.$on("workspace:resetWorkspace", () => {
      setWorkspace({ ...defaultWorkspace });
      setPersisted(false);
      setReports([]);
      erroredReportsRef.current.clear();
    });
    return () => {
      EventBus.$off("workspace:resetWorkspace");
    };
  }, []);

  // load the workspace from the server if id is set
  useEffect(() => {
    if (id) {
      getCustomWorkspace(id).then((workspace) => {
        setWorkspace(workspace);
        setPersisted(true);
      });
      getCustomWorkspaceReports(id).then((reports) => {
        setReports(reports);
        setMetricsLoading(false);
      });
    } else {
      setWorkspace({ ...defaultWorkspace });
      setPersisted(false);
    }
  }, [id, currentAccountId]);

  // Clear errored reports when account changes
  useEffect(() => {
    erroredReportsRef.current.clear();
  }, [currentAccountId]);

  const handleReportError = useCallback((reportId: string) => {
    erroredReportsRef.current.add(reportId);
    // Force a re-render only when needed
    setReports((prev) => [...prev]);
  }, []);

  const [reports, setReports] = useState<ReportType[]>([]);
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const navigate = useNavigate();
  const workspaceButtonText = useMemo(() => {
    return persisted ? "Update Name" : "Create Workspace";
  }, [persisted]);

  const [localReportConfigs, setLocalReportConfigs] = useState<{
    [key: string]: ReportConfigV2;
  }>({});

  useEffect(() => {
    const newConfigs = reports
      .filter((report) => report?.id)
      .reduce(
        (acc, report) => {
          // Only update if the config has actually changed
          if (
            !localReportConfigs[report.id] ||
            JSON.stringify(localReportConfigs[report.id]) !==
              JSON.stringify(report.config)
          ) {
            acc[report.id] = report.config;
          } else {
            acc[report.id] = localReportConfigs[report.id];
          }
          return acc;
        },
        {} as { [key: string]: ReportConfigV2 },
      );

    // Only update if there are actual changes
    if (JSON.stringify(newConfigs) !== JSON.stringify(localReportConfigs)) {
      setLocalReportConfigs(newConfigs);
    }
  }, [reports, localReportConfigs]);

  const setWorkspaceName = (title: string) => {
    setWorkspace((prev: CustomWorkspace) => ({
      ...prev,
      title,
    }));
  };

  const handleReportClick = async (reportId: string) => {
    setIsChartModalOpen(false);
    try {
      workspace.report_ids.push(reportId);
      await saveCustomWorkspace(workspace);
      const updatedReports = await getCustomWorkspaceReports(workspace.id);
      setReports(updatedReports);

      // Update local report configs
      const newConfigs = updatedReports.reduce(
        (acc: { [key: string]: ReportConfigV2 }, report: ReportType) => {
          acc[report.id] = report.config;
          return acc;
        },
        {},
      );
      setLocalReportConfigs(newConfigs);
    } catch (error) {
      console.error("Error creating report", error);
    }
  };

  const handleRemoveReport = useCallback(
    async (reportId: string) => {
      try {
        workspace.report_ids.splice(workspace.report_ids.indexOf(reportId), 1);
        await saveCustomWorkspace(workspace);
        const updatedReports = await getCustomWorkspaceReports(workspace.id);
        setReports(updatedReports);

        // Update local report configs
        setLocalReportConfigs(() => {
          const newConfigs = updatedReports.reduce(
            (acc: { [key: string]: ReportConfigV2 }, report: ReportType) => {
              acc[report.id] = report.config;
              return acc;
            },
            {},
          );
          return newConfigs;
        });
      } catch (error) {
        console.error("Error removing report from workspace page:", error);
      }
    },
    [workspace],
  );

  const renderReport = useCallback(
    (report: ReportType) => {
      if (!report?.id || erroredReportsRef.current.has(report?.id)) {
        return null;
      }

      return (
        <div
          key={report.id}
          className="w-full h-auto overflow-hidden [&:fullscreen]:h-screen [&:fullscreen]:overflow-y-auto"
          id={`report-${report.id}`}
        >
          <ReportV2
            id={report.id}
            reportConfig={localReportConfigs[report.id] || report.config}
            newFilterField={null}
            onEdit={() => {
              try {
                navigate(`/areport/${report.id}`);
              } catch (error) {
                console.error("Error navigating to report:", error);
                window.location.href = `/report/${report.id}`;
              }
            }}
            size="md"
            onRemoveFromPage={() => handleRemoveReport(report.id)}
            isSystemReport={!!report.system_report_name}
            onError={() => handleReportError(report.id)}
            disableSummaryRegeneration={true}
          />
        </div>
      );
    },
    [localReportConfigs, handleRemoveReport, handleReportError, navigate],
  );

  const handleSaveWorkspace = async () => {
    try {
      const updatedWorkspace = await saveCustomWorkspace(workspace);
      setWorkspace(updatedWorkspace);
      if (!persisted) {
        setPersisted(true);
        navigate(`/workspace/${updatedWorkspace.id}`);
      }
      EventBus.$emit("sidebar:refreshCustomWorkspaces");
    } catch (error) {
      console.error("Error saving workspace:", error);
    }
  };

  return (
    <div className="flex w-full relative justify-center">
      <div className="border-r border-gray-200 bg-white p-6 w-full max-w-6xl dark:bg-gray-800 dark:border-gray-700">
        <img
          src={paritaLogoCopilot}
          alt="PeopleScape Logo"
          className="w-[600px] mx-auto h-auto"
        />

        <div className="flex items-center justify-between mb-4">
          {/* When report isn't saved, show the title field and save button */}
          <div className="flex-1 space-y-4 min-w-[400px]">
            <div className="flex items-center space-x-2">
              <div className="flex-1">
                <Input
                  type="text"
                  placeholder="Untitled Workspace"
                  value={workspace.title}
                  onChange={(e) => setWorkspaceName(e.target.value)}
                  className="w-full rounded-md border-gray-300 border text-xl"
                />
              </div>
              <button
                onClick={handleSaveWorkspace}
                disabled={!workspace.title}
                className="px-4 py-2 bg-sky-600 text-white rounded-lg hover:bg-sky-600 disabled:opacity-50"
              >
                {workspaceButtonText}
              </button>
            </div>
          </div>
        </div>

        {persisted && (
          <div className="flex gap-6">
            <div className="flex-1 space-y-4 min-w-[400px]">
              {metricsLoading ? (
                // Loading skeleton for reports
                [...Array(2)].map((_, index) => (
                  <div
                    key={index}
                    className="w-full border rounded-lg p-4 dark:border-gray-700"
                  >
                    <div className="animate-pulse">
                      <div className="h-48 bg-gray-200 dark:bg-gray-700 rounded-lg mb-4"></div>
                      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                    </div>
                  </div>
                ))
              ) : (
                <>
                  {reports.map((report: ReportType) => renderReport(report))}

                  <div className="flex">
                    <button
                      onClick={() => setIsChartModalOpen(true)}
                      className="w-1/3 aspect-square border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg flex flex-col items-center justify-center p-4 hover:border-gray-400 dark:hover:border-gray-500 transition-colors"
                    >
                      <Plus className="w-8 h-8 text-gray-400 dark:text-gray-500 mb-2" />
                      <span className="text-sm text-gray-500 dark:text-gray-400">
                        Add your own report
                      </span>
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <Dialog open={isChartModalOpen} onOpenChange={setIsChartModalOpen}>
          <DialogContent className="sm:max-w-[80%] h-[80vh]">
            <DialogHeader>
              <DialogTitle>Select Report</DialogTitle>
              <DialogDescription>
                Choose a report to add to your dashboard
              </DialogDescription>
            </DialogHeader>
            <Reports onReportClick={handleReportClick} />
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CustomWorkspace;
